import { woodPanel, copperButton, sectionHeading, gameColors } from '@/shared/ui/game-theme';
import { useState } from 'react';
import type {
  AdviceCommand,
  CharacterEncounter,
  CharacterOption,
  DecisionReceipt,
} from '../types/character.types';
import {
  characterLabel,
  type CharacterLanguage,
} from '../utils/character-labels';
import {
  encounterActionLabel,
  encounterDescription,
  encounterTravelSeconds,
  encounterRiskLabel,
} from '../utils/encounter-copy';
import { EncounterRoster } from './encounter-roster';
import { encounterHeroStatusText, encounterOffersDecision, encounterShowsRoster, encounterStageTitle, encounterWaitingText } from '../utils/encounter-stage';
import './hero-character.css';
export interface EncounterInteraction {
  pending: AdviceCommand | null;
  sending: boolean;
  error: string | null;
  onAdvise: (option: CharacterOption) => void;
  onRetry: () => void;
  onDismiss: () => void;
}
export function EncounterOverlay({
  encounter,
  decision,
  language,
  interaction,
  simulationPaused,
}: {
  encounter: CharacterEncounter | null;
  decision: DecisionReceipt | null;
  language: CharacterLanguage;
  interaction: EncounterInteraction;
  simulationPaused: boolean;
}) {
  const [collapsedStage, setCollapsedStage] = useState<string | null>(null);
  if (!encounter || (encounter.status !== 'active' && !decision)) return null;
  const command = encounter.advice?.command;
  const originalAdvice = decision?.suggestedActionId ?? (decision && command && decision.adviceCommandId === command.commandId
    && decision.instanceId === encounter.id && command.instanceId === decision.instanceId
    && command.stageRevision === decision.fromRevision ? command.suggestedActionId : null);
  const stageKey = `${encounter.id}:${encounter.stageId}`;
  const collapsed = collapsedStage === stageKey;
  const hasDecision = encounterOffersDecision(encounter);
  const heroStatusText = encounterHeroStatusText(encounter.knownContext?.battleSummary, language);
  const ru = language === 'ru';
  const { pending, sending, error, onAdvise, onRetry, onDismiss } = interaction;
  return (
    <section
      className="hero-character encounter-overlay"
      style={woodPanel}
      aria-label={ru ? 'Встреча героя' : 'Hero encounter'}
    >
      <header className="encounter-heading">
        <h3 style={sectionHeading}>{encounterStageTitle(encounter.stageId, language)}</h3>
        <button style={copperButton} type="button" aria-expanded={!collapsed}
          aria-label={collapsed ? (ru ? 'Развернуть встречу' : 'Expand encounter') : (ru ? 'Свернуть встречу' : 'Minimize encounter')}
          onClick={() => setCollapsedStage(collapsed ? null : stageKey)}>
          {collapsed ? (ru ? 'Развернуть' : 'Expand') : (ru ? 'Свернуть' : 'Minimize')}
        </button>
      </header>
      {collapsed && (
        <p className="character-muted" aria-live="polite">
          {error ? characterLabel(language, error)
            : sending ? (ru ? 'Передаём совет…' : 'Sending advice…')
            : encounter.status !== 'active' && decision ? characterLabel(language, decision.reaction)
            : simulationPaused ? (ru ? 'Встреча приостановлена.' : 'The encounter is paused.')
            : !hasDecision ? encounterWaitingText(encounter.stageId, language)
            : decision && decision.toRevision >= encounter.revision ? characterLabel(language, decision.reaction)
            : pending || encounter.advice ? (ru ? 'Совет записан. Ждём решения героя.' : 'Advice recorded. Waiting for the hero’s decision.')
            : `${ru ? 'На решение осталось' : 'Time remaining'}: ${Math.ceil(encounter.remainingSeconds)} ${ru ? 'сек.' : 's'}`}
        </p>
      )}
      <div hidden={collapsed}>
      <p>{encounterDescription(encounter, language)}</p>
      {heroStatusText && <p role="status">{heroStatusText}</p>}
      {simulationPaused && encounter.status === 'active' && <p role="status">{ru ? 'Мир на паузе. Встреча продолжится после возобновления.' : 'The world is paused. The encounter will continue when it resumes.'}</p>}
      {encounterShowsRoster(encounter.stageId) && <EncounterRoster participants={encounter.participants} metadata={encounter.knownContext?.participantMetadata} language={language} />}
      {encounter.status === 'active' && !hasDecision && !simulationPaused && <p role="status">{encounterWaitingText(encounter.stageId, language)}</p>}
      {hasDecision && (
        <>
          <p className="character-muted">
            {ru ? 'На решение осталось' : 'Time remaining'}:{' '}
            {Math.ceil(encounter.remainingSeconds)} {ru ? 'сек.' : 's'}
          </p>
          {encounter.inclinationActionId && (
            <p>
              {ru ? 'Герой склоняется к решению:' : 'The hero is inclined to:'}{' '}
              {encounterActionLabel(
                encounter,
                language,
                encounter.inclinationActionId,
              )}
            </p>
          )}
          <p>
            {ru
              ? 'Вы можете посоветовать. Решение остаётся за героем.'
              : 'You can offer advice. The hero makes the final decision.'}
          </p>
          <div className="character-options">
            {encounter.options.map((option) => (
              <button style={copperButton}
                key={option.id}
                type="button"
                disabled={
                  simulationPaused ||
                  !option.available ||
                  !!pending ||
                  sending ||
                  !!encounter.advice ||
                  !!(decision && decision.toRevision > encounter.revision)
                }
                onClick={() => onAdvise(option)}
              >
                <strong>
                  {encounterActionLabel(encounter, language, option.id)}
                </strong>
                {option.cost && (
                  <small>
                    {ru ? 'Зелья' : 'Potions'}: {option.cost.potions},{' '}
                    {ru ? 'монеты' : 'gold'}: {option.cost.gold}
                  </small>
                )}
                {encounterTravelSeconds(encounter, option.id) !== null && (
                  <small>
                    {ru ? 'Время пути' : 'Travel time'}:{' '}
                    {Math.ceil(
                      encounterTravelSeconds(encounter, option.id)! / 60,
                    )}{' '}
                    {ru ? 'мин.' : 'min'}
                  </small>
                )}
                {option.knownRisk && <small>{encounterRiskLabel(language, option.knownRisk)}</small>}
                {option.reasons?.map((reason) => (
                  <small key={reason}>{characterLabel(language, reason)}</small>
                ))}
              </button>
            ))}
          </div>
        </>
      )}
      {sending && (
        <p role="status">{ru ? 'Передаём совет…' : 'Sending advice…'}</p>
      )}
      {!sending && pending && !error && (
        <p role="status">
          {ru
            ? 'Совет записан. Ждём решения героя.'
            : 'Advice recorded. Waiting for the hero’s decision.'}
        </p>
      )}
      {error && <p role="alert" style={{ color: gameColors.danger }}>{characterLabel(language, error)}</p>}
      {error && pending && !sending && !simulationPaused && (
        <button style={copperButton} type="button" onClick={onRetry}>
          {ru ? 'Повторить тот же совет' : 'Retry the same advice'}
        </button>
      )}
      {decision && (
        <div aria-live="polite">
          <p>
            {decision.toRevision < encounter.revision && <span className="character-muted">{ru ? 'Предыдущее решение: ' : 'Previous decision: '}</span>}
            <strong>{characterLabel(language, decision.reaction)}</strong>
          </p>
          {originalAdvice && <p>{ru ? 'Ваш совет: ' : 'Your advice: '}{encounterActionLabel(encounter, language, originalAdvice)}</p>}
          <p>
            {ru ? 'Решение героя: ' : 'The hero’s choice: '}{encounterActionLabel(encounter, language, decision.chosenActionId)}
          </p>
          {decision.reasons.map((reason) => (
            <p key={reason} className="character-muted">
              {characterLabel(language, reason)}
            </p>
          ))}
          <button style={copperButton} type="button" onClick={onDismiss}>
            {ru ? 'Понятно' : 'Got it'}
          </button>
        </div>
      )}
      </div>
    </section>
  );
}
